import {CallErrorCode, CallParty, MatrixCall, CallState, CallType as MatrixMediaCallType} from "matrix-js-sdk/lib/webrtc/call";
import disconnectSound from '../assets/sounds/disconnected.mp3';
import logo from '../assets/logo.svg';
import {VoiceHandler} from "../components/Chat/VoiceCallHandler.tsx";
import {matrixService} from "./matrixService.ts";
import {AppDispatch, store} from "../store/store.ts";
import {
    playConnectingSound, playRingback, playRingtone,
    setCallFrom,
    setCallState, setCallTo,
    setDialerHidden,
    setIncomingCall, stopConnectingSound, stopRingback, stopRingtone,
    engineCallOf, tagEngine
} from "../store/features/calls/dialerSlice.ts";
import {CallStateEnum} from "../components/Dialer/callState";
import {User, usersApi} from "../store/features/Users/userService.ts";
import {
    CallType,
    clearCallMetadata,
    fetchCallMetadata,
    findMatrixCallMetadataForInvite,
    MATRIX_CALL_METADATA_EVENT_TYPE,
    sendMatrixEvent,
    EventType,
    setCurrentCallData,
    setCallMetadata
} from "../store/features/MatrixEvents/MatrixEventsService.tsx";
import logger from "./logger.ts";
import {beginCallContext, clearCallContext} from "./callCorrelation";
import {
    MatrixCallCoordinator,
    type MatrixCallEnginePort,
    type MatrixCallLogPort,
} from "./matrixCalling/MatrixCallCoordinator.ts";
import {
    isSameMatrixCall,
    type MatrixCallIdentity,
    type MatrixCallMediaState,
    type MatrixCallSessionState,
} from "./matrixCalling/CallSessionState.ts";

import {buildCallRingingContent, MATRIX_CALL_RINGING_EVENT_TYPE} from './matrixCalling/callRinging';
import {persistCallTermination, flushCallAccounting, type CallAccountingContext} from './matrixCalling/CallAccountingOutbox';
import {recordLocalCallTermination} from './matrixCalling/CallStateRepair';
import {mediaErrorName} from './matrixCalling/mediaRequestGuard';
import {showToast} from '../store/features/Notifications/notificationsSlice';
import { focusAppWindow } from '../utils/desktopBridge';

const accountingContexts = new Map<string, CallAccountingContext>();
const terminalRecorded = new Set<string>();
let outgoingSetupInFlight = false;
let lastEngineError: MatrixCallErrorData | null = null;

const CAMERA_UNAVAILABLE_TOAST = {
    description: 'Camera is unavailable. Allow it for this site in the browser settings and close other apps that use it.',
    type: 'error',
    position: 'center',
    icon: 'videocam_off',
} as const;

const MICROPHONE_BLOCKED_TOAST = {
    description: 'Microphone is blocked. Allow it for this site in the browser settings to make calls.',
    type: 'error',
    position: 'center',
    icon: 'mic_off',
} as const;

const isSilentLocalFailure = (reason: string): reason is CallErrorCode.NoUserMedia | CallErrorCode.LocalOfferFailed =>
    reason === CallErrorCode.NoUserMedia || reason === CallErrorCode.LocalOfferFailed;

const STATES_BEFORE_INVITE: ReadonlySet<CallState> = new Set([CallState.Fledgling, CallState.WaitLocalMedia, CallState.CreateOffer]);

const STATES_BEFORE_ANSWER: ReadonlySet<CallState> = new Set([CallState.Ringing, CallState.WaitLocalMedia, CallState.CreateAnswer]);

type MediaPermission = PermissionState | 'unknown';

const readMediaPermission = async (name: 'camera' | 'microphone'): Promise<MediaPermission> => {
    try {
        const status = await navigator.permissions?.query({name: name as PermissionName});
        return status?.state ?? 'unknown';
    } catch {
        return 'unknown';
    }
};

const readCallPermissions = async (video: boolean): Promise<{microphone: MediaPermission; camera?: MediaPermission}> => ({
    microphone: await readMediaPermission('microphone'),
    ...(video ? {camera: await readMediaPermission('camera')} : {}),
});

export function getActiveMatrixCallIdentity(): MatrixCallIdentity | undefined {
    const state = matrixCallCoordinator.getState();
    return state.kind === 'idle' || state.kind === 'ended' || state.kind === 'failed' ? undefined : state.call;
}

export function getActiveDirectCall(): (MatrixCallIdentity & {answered: boolean}) | undefined {
    const state = matrixCallCoordinator.getState();
    if (state.kind === 'idle' || state.kind === 'ended' || state.kind === 'failed') return undefined;
    return {...state.call, answered: state.kind === 'connected' || state.kind === 'reconnecting'};
}

export function getMatrixCallLocalMedia(): {microphoneEnabled: boolean; cameraEnabled: boolean} {
    return {
        microphoneEnabled: !!currentMatrixCall && !currentMatrixCall.isMicrophoneMuted(),
        cameraEnabled: matrixCallCoordinator.getMedia()?.localVideo ?? false,
    };
}

function captureAccountingContext(call: MatrixCallIdentity, metadata: {metadata: string; callId: string}): CallAccountingContext {
    const auth = store.getState().auth;
    if (!auth.tenant) throw new Error('Accounting tenant unavailable');
    const context = {tenant: auth.tenant, eventOwner: Number(auth.userId), metadata: metadata.metadata,
        callId: metadata.callId, matrixCallId: call.matrixCallId, roomId: call.roomId,
        clientSessionId: currentMatrixCall?.ourPartyId ?? ''};
    if (!context.tenant || !context.clientSessionId || !context.eventOwner) throw new Error('Accounting session unavailable');
    accountingContexts.set(call.matrixCallId, context);
    beginCallContext(call.matrixCallId, metadata.callId);
    return context;
}

const disconnectAudioRef = new Audio(disconnectSound);

let matrixVoiceHandler: VoiceHandler | null = null;

let currentMatrixCall: MatrixCall | null = null;
let handlersInitialized = false;

export interface MatrixCallSessionSnapshot {
    readonly state: MatrixCallSessionState;
    readonly call: MatrixCall | null;
}

const sessionListeners = new Set<() => void>();
let sessionSnapshot: MatrixCallSessionSnapshot = {state: {kind: "idle"}, call: null};

const publishSession = (): void => {
    const state = matrixCallCoordinator.getState();
    if (sessionSnapshot.state === state && sessionSnapshot.call === currentMatrixCall) return;
    sessionSnapshot = {state, call: currentMatrixCall};
    sessionListeners.forEach(listener => listener());
};

function setCurrentMatrixCall(call: MatrixCall | null): void {
    currentMatrixCall = call;
    publishSession();
}

export const subscribeMatrixCallSession = (listener: () => void): (() => void) => {
    sessionListeners.add(listener);
    return () => {
        sessionListeners.delete(listener);
    };
};

export const getMatrixCallSessionSnapshot = (): MatrixCallSessionSnapshot => sessionSnapshot;
let lastFinalizedMatrixCallId: string | null = null;
let awaitingRingingAckCallId: string | null = null;
let preInviteCallId: string | null = null;
let bufferedRingingAckCallId: string | null = null;
const ringingAcknowledgedCallIds = new Set<string>();
const MAX_ACKNOWLEDGED_CALL_IDS = 200;

const stopOutgoingCallSounds = (dispatch: AppDispatch): void => {
    awaitingRingingAckCallId = null;
    preInviteCallId = null;
    bufferedRingingAckCallId = null;
    dispatch(stopRingback());
    dispatch(stopConnectingSound());
};

export const onRemoteRingingAcknowledged = (callId: string, dispatch: AppDispatch): boolean => {
    dispatch = tagEngine('matrix', dispatch);
    if (currentMatrixCall?.callId !== callId) return false;
    const state = matrixCallCoordinator.getState();
    if (state.kind !== 'connecting' || state.call.matrixCallId !== callId) return false;
    if (awaitingRingingAckCallId !== callId) {
        if (preInviteCallId !== callId || bufferedRingingAckCallId === callId) return false;
        bufferedRingingAckCallId = callId;
        return true;
    }
    awaitingRingingAckCallId = null;
    dispatch(stopConnectingSound());
    dispatch(playRingback());
    logger.info('Remote ringing acknowledged', {
        source: 'matrix_call',
        event: 'remote_ringing_acknowledged',
        callId,
        roomId: state.call.roomId,
    });
    return true;
};

const sendRingingAcknowledgement = (call: MatrixCall, roomId: string): void => {
    const callId = call.callId;
    if (ringingAcknowledgedCallIds.has(callId)) return;
    ringingAcknowledgedCallIds.add(callId);
    if (ringingAcknowledgedCallIds.size > MAX_ACKNOWLEDGED_CALL_IDS) {
        const oldest = ringingAcknowledgedCallIds.values().next().value;
        if (oldest) ringingAcknowledgedCallIds.delete(oldest);
    }
    try {
        const client = matrixService.getClient();
        if (!client) {
            ringingAcknowledgedCallIds.delete(callId);
            return;
        }
        void client.sendEvent(roomId, MATRIX_CALL_RINGING_EVENT_TYPE, buildCallRingingContent(call)).catch((error: unknown) => {
            ringingAcknowledgedCallIds.delete(callId);
            logger.warn('Failed to send call ringing acknowledgement', {
                source: 'matrix_call',
                event: 'ringing_ack_send_failed',
                callId,
                roomId,
                error: error instanceof Error ? error.message : String(error),
            });
        });
    } catch (error) {
        ringingAcknowledgedCallIds.delete(callId);
        logger.warn('Failed to send call ringing acknowledgement', {
            source: 'matrix_call',
            event: 'ringing_ack_send_failed',
            callId,
            roomId,
            error: error instanceof Error ? error.message : String(error),
        });
    }
};

interface IncomingMatrixCallData {
    readonly call: MatrixCall;
    readonly callerUserId: string;
    readonly callerName?: string;
    readonly callId: string;
    readonly roomId: string;
    readonly isVideo?: boolean;
}

interface MatrixCallStateChangedData {
    readonly callId: string;
    readonly roomId: string;
    readonly newState: CallState;
    readonly oldState: CallState;
    readonly hangupParty?: CallParty;
    readonly hangupReason?: string;
}

interface MatrixCallFeedsChangedData {
    readonly callId: string;
    readonly localFeeds: readonly unknown[];
    readonly remoteFeeds: readonly unknown[];
}

interface MatrixCallErrorData {
    readonly callId: string;
    readonly roomId?: string;
    readonly error: unknown;
    readonly code?: string;
    readonly action?: string;
}

interface MatrixCallEndedData {
    readonly callId?: string;
    readonly call_id?: string;
    readonly roomId?: string;
    readonly reason?: string;
    readonly hangupParty?: CallParty;
    readonly oldState?: CallState;
}

interface MatrixCallRingingData {
    readonly callId: string;
    readonly roomId: string;
    readonly senderId: string;
}

interface MatrixCallReplacedData {
    readonly oldCallId: string;
    readonly newCallId: string;
    readonly roomId: string;
    readonly call: MatrixCall;
}

const legacyMatrixCallEngine: MatrixCallEnginePort = {
    createVoiceCall(request) {
        const call = matrixService.prepareCall(request.roomId);
        setCurrentMatrixCall(call);
        preInviteCallId = call.callId;
        bufferedRingingAckCallId = null;
        return {
            matrixCallId: call.callId,
            roomId: call.roomId,
            remoteUserId: request.remoteUserId ?? matrixService.directPeerOf(call.roomId),
            backendCallId: request.backendCallId,
        };
    },
    async startVoiceCall(call, options) {
        if (!currentMatrixCall || currentMatrixCall.callId !== call.matrixCallId) {
            throw new Error('Prepared Matrix call is no longer active');
        }
        await matrixService.placePreparedCall(currentMatrixCall, options.video);
    },
    async accept(call, options) {
        await matrixService.answerCall(call.matrixCallId, options.video === true);
    },
    async reject(call) {
        await matrixService.rejectCall(call.matrixCallId);
    },
    async hangUp(call) {
        await matrixService.hangupCall(call.matrixCallId);
    },
    async setMuted(muted) {
        if (!currentMatrixCall) {
            throw new Error("No active Matrix call");
        }
        await currentMatrixCall.setMicrophoneMuted(muted);
    },
    setOutputVolume(volume) {
        if (matrixVoiceHandler && currentMatrixCall) {
            matrixVoiceHandler.setRemoteVolume(currentMatrixCall.callId, volume);
        }
    },
    async setLocalVideo(enabled) {
        if (!currentMatrixCall) {
            throw new Error("No active Matrix call");
        }
        const muted = await currentMatrixCall.setLocalVideoMuted(!enabled);
        return !muted;
    },
    async setScreenSharing(enabled) {
        if (!currentMatrixCall) {
            throw new Error("No active Matrix call");
        }
        return currentMatrixCall.setScreensharingEnabled(enabled, {audio: false});
    },
};

export const readMatrixCallMedia = (call: MatrixCall, intent: MatrixCallMediaState['intent']): MatrixCallMediaState => ({
    intent,
    localVideo: call.hasLocalUserMediaVideoTrack && !call.isLocalVideoMuted(),
    remoteVideo: call.hasRemoteUserMediaVideoTrack && !(call.remoteUsermediaFeed?.isVideoMuted() ?? true),
    localScreenShare: call.isScreensharing(),
    remoteScreenShare: !!call.remoteScreensharingFeed,
});

const callLog: MatrixCallLogPort = {
    transition(previous, next, event) {
        const call = "call" in event ? event.call : undefined;
        logger.info(
            `[MATRIX CALL] ${previous.kind} -> ${next.kind}`,
            {
                source: "matrix_call",
                event: "state_transition",
                previousState: previous.kind,
                nextState: next.kind,
                transition: event.type,
                callId: call?.matrixCallId,
                roomId: call?.roomId,
            },
        );
    },
};

const matrixCallCoordinator = new MatrixCallCoordinator(
    legacyMatrixCallEngine,
    callLog,
);
matrixCallCoordinator.subscribe(() => publishSession());

const identityFromCurrentCall = (
    callId?: string,
    roomId?: string,
): MatrixCallIdentity | null => {
    const resolvedCallId = callId ?? currentMatrixCall?.callId;
    const resolvedRoomId = roomId ?? currentMatrixCall?.roomId;
    if (!resolvedCallId || !resolvedRoomId) return null;

    return { matrixCallId: resolvedCallId, roomId: resolvedRoomId };
};

export const MATRIX_CALL_DECLINED_REASON = 'declined';
export const MATRIX_CALL_BUSY_REASON = 'user_busy';

const isActiveCoordinatorCall = (
    state: MatrixCallSessionState,
    call: MatrixCallIdentity,
): boolean =>
    (state.kind === "ringing" ||
        state.kind === "connecting" ||
        state.kind === "connected" ||
        state.kind === "reconnecting") &&
    state.call.matrixCallId === call.matrixCallId &&
    state.call.roomId === call.roomId;

const getAuthenticatedUserId = (): number | null => {
    const userId = Number(store.getState().auth?.userId);

    return Number.isFinite(userId) && userId > 0 ? userId : null;
};
const readIncomingCallMetadata = (roomId: string, matrixCallId: string) => {
    const room = matrixService.getClient()?.getRoom(roomId);
    return findMatrixCallMetadataForInvite(
        room?.getLiveTimeline()?.getEvents(),
        matrixCallId,
    );
};

const recordLocalSessionTermination = (
    call: MatrixCallIdentity,
    terminalType: 'HANGUP' | 'REJECT' | 'TECHNICAL_CLOSURE',
    cause?: string,
): void => {
    const auth = store.getState().auth;
    const context = accountingContexts.get(call.matrixCallId);
    const tenant = context?.tenant ?? auth.tenant;
    const owner = context?.eventOwner ?? Number(auth.userId);
    const sessionId = context?.clientSessionId ??
        (currentMatrixCall?.callId === call.matrixCallId ? currentMatrixCall.ourPartyId : undefined);
    if (!tenant || !Number.isSafeInteger(owner) || owner <= 0 || !sessionId) return;
    try {
        recordLocalCallTermination({
            get: key => localStorage.getItem(key) ?? undefined,
            set: (key, value) => localStorage.setItem(key, value),
            remove: key => localStorage.removeItem(key),
            keys: prefix => Object.keys(localStorage).filter(key => key.startsWith(prefix)),
        }, {scope: 'phonehq:repair:' + JSON.stringify([tenant, owner]),
            matrixCallId: call.matrixCallId, roomId: call.roomId, sessionId,
            callId: context ? Number(context.callId) : undefined,
            terminalType, occurredAt: Date.now()});
        logger.info('Matrix call termination recorded locally', {
            source: 'matrix_call', event: 'local_evidence_recorded', matrixCallId: call.matrixCallId, roomId: call.roomId,
            terminalType, cause,
        });
    } catch {
        logger.warn('Local Matrix session termination could not be persisted', {
            source: 'matrix_call', event: 'local_evidence_storage_failed', matrixCallId: call.matrixCallId, roomId: call.roomId,
        });
    }
};

const recordBackendTermination = (
    call: MatrixCallIdentity,
    type: EventType.HANGUP | EventType.REJECT,
    failureCause?: string,
): void => {
    if (terminalRecorded.has(call.matrixCallId)) return;
    const auth = store.getState().auth;
    const occurredAt = Date.now();
    try {
        recordLocalSessionTermination(call, failureCause ? 'TECHNICAL_CLOSURE' : type, failureCause);
        const context = accountingContexts.get(call.matrixCallId);
        if (!context) {
            logger.warn('Matrix termination requires history reconciliation', {
                source: 'matrix_call', event: 'accounting_context_missing', matrixCallId: call.matrixCallId, roomId: call.roomId,
            });
            return;
        }
        persistCallTermination({context, type, occurredAt});
        terminalRecorded.add(call.matrixCallId);
        // Completed contexts are durable; retaining them in memory would grow with every call.
        accountingContexts.delete(call.matrixCallId);
        const oldestRecorded = terminalRecorded.values().next().value;
        if (terminalRecorded.size > 200 && oldestRecorded) terminalRecorded.delete(oldestRecorded);
        void flushCallAccounting();
    } catch {
        // Storage denial must not prevent Matrix signaling or local call teardown.
        logger.warn('Matrix termination could not be persisted locally', {
            source: 'matrix_call', event: 'accounting_storage_failed', tenant: auth.tenant ?? undefined,
            matrixCallId: call.matrixCallId, roomId: call.roomId,
        });
    }
};

const setIncomingCallData = (
    dispatch: AppDispatch,
    params: {
        matrixCallId: string;
        roomId: string;
        caller: number;
        backendCallId?: string;
    },
) => {
    const authenticatedUserId = getAuthenticatedUserId();

    dispatch(setCurrentCallData({
        callId: params.backendCallId ?? params.matrixCallId,
        caller: params.caller,
        callees: authenticatedUserId ? [authenticatedUserId] : [],
        roomId: params.roomId,
        startedAt: Date.now(),
        endedAt: null,
        status: 'ONGOING'
    }));
};

/**
 * Initialize Matrix call handlers - equivalent to initializeSip
 */
export const initializeMatrixCallHandlers = (dispatch: AppDispatch): void => {
    if (handlersInitialized) return;
    dispatch = tagEngine('matrix', dispatch);
    matrixVoiceHandler = new VoiceHandler();
    handlersInitialized = true;

    matrixService.on('incomingCall', (callData: IncomingMatrixCallData) => {
        handleIncomingMatrixCall(callData, dispatch);
    });

    matrixService.on('callStateChanged', (data: MatrixCallStateChangedData) => {
        handleMatrixCallStateChange(data, dispatch);
    });

    matrixService.on('callFeedsChanged', (data: MatrixCallFeedsChangedData) => {
        handleMatrixCallFeeds(data);
    });

    matrixService.on('callError', (data: MatrixCallErrorData) => {
        handleMatrixCallError(data, dispatch);
    });

    matrixService.on('callEnded', (data: MatrixCallEndedData) => {
        handleMatrixCallEnded(data, dispatch);
    });

    matrixService.on('callReplaced', (data: MatrixCallReplacedData) => {
        handleMatrixCallReplaced(data);
    });

    matrixService.on('callRinging', (data: MatrixCallRingingData) => {
        if (data.senderId === matrixService.getCurrentUserMatrixId()) return;
        onRemoteRingingAcknowledged(data.callId, dispatch);
    });

    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
        Notification.requestPermission();
    }
};

/**
 * Handle incoming Matrix call - equivalent to SIP newRTCSession for incoming
 */
const handleIncomingMatrixCall = (
    callData: IncomingMatrixCallData,
    dispatch: AppDispatch,
): void => {
    const {call, callerUserId, callerName, callId, roomId} = callData;
    const metadataContent = readIncomingCallMetadata(roomId, callId);
    const incomingIdentity = {
        matrixCallId: callId,
        roomId,
        remoteUserId: callerUserId,
        backendCallId: metadataContent?.callId,
    };

    if (outgoingSetupInFlight) {
        void matrixService.rejectCallAsBusy(callId);
        return;
    }
    if (store.getState().dialer.callState === CallStateEnum.GROUP_CALL) {
        logger.warn('Rejecting incoming Matrix call during a group call', {
            source: 'matrix_call',
            event: 'incoming_call_rejected_group_call',
            callId,
            roomId,
        });
        dispatch(setIncomingCall(false));
        void matrixService.rejectCallAsBusy(callId).catch(error => {
            logger.error('Failed to reject incoming Matrix call during a group call', {
                source: 'matrix_call',
                event: 'incoming_call_reject_failed',
                callId,
                roomId,
                error: error instanceof Error ? error.message : String(error),
            });
        });
        return;
    }
    const incomingIntent = callData.isVideo || call.type === MatrixMediaCallType.Video ? 'video' : 'audio';
    matrixCallCoordinator.registerIncoming(incomingIdentity, Date.now(), incomingIntent);
    if (!isActiveCoordinatorCall(matrixCallCoordinator.getState(), incomingIdentity)) {
        logger.warn('Ignoring incoming Matrix call while another call is active', {
            source: 'matrix_call',
            event: 'incoming_call_ignored_busy',
            callId,
            roomId,
        });
        void matrixService.rejectCallAsBusy(callId).catch(error => {
            logger.error('Failed to reject ignored incoming Matrix call', {
                source: 'matrix_call',
                event: 'incoming_call_reject_failed',
                callId,
                roomId,
                error: error instanceof Error ? error.message : String(error),
            });
        });
        return;
    }
    lastFinalizedMatrixCallId = null;

    dispatch(clearCallMetadata());
    if (metadataContent) {
        dispatch(setCallMetadata(metadataContent));
    }
    setCurrentMatrixCall(call);
    beginCallContext(callId, metadataContent?.callId);
    if (metadataContent) captureAccountingContext(incomingIdentity, metadataContent);
    setIncomingCallData(dispatch, {
        matrixCallId: callId,
        backendCallId: metadataContent?.callId,
        caller: 0,
        roomId,
    });
    dispatch(setDialerHidden(false));
    if (callerName) {
        dispatch(setCallFrom({
            firstName: callerName.split(' ')[0] || callerName,
            lastName: callerName.split(' ').slice(1).join(' ') || '',
            userId: callerUserId,
            matrixCallId: callId,
            roomId,
            ...(incomingIntent === 'video' ? {media: 'video'} : {}),
        }));

        // Resolve Matrix ID to user ID via backend API
        void dispatch(usersApi.endpoints.resolveMatrixIds.initiate({matrixIds: [callerUserId]}))
            .unwrap()
            .then(userIdMap => {
                if (!isActiveCoordinatorCall(matrixCallCoordinator.getState(), incomingIdentity)) {
                    return;
                }
                const userId = userIdMap[callerUserId];

                setIncomingCallData(dispatch, {
                    matrixCallId: callId,
                    backendCallId: metadataContent?.callId,
                    caller: userId ? Number(userId) : 0,
                    roomId,
                });
            })
            .catch((error: unknown) => {
                logger.error('Failed to resolve Matrix ID for incoming call', {
                    source: 'matrix_call',
                    event: 'caller_resolution_failed',
                    callId,
                    roomId,
                    error: error instanceof Error ? error.message : String(error),
                });
            });
    } else {
        dispatch(setCallFrom(callerUserId));
    }

    if ("Notification" in window) {
        if (Notification.permission === "granted") {
            const notification = new Notification(callerName || callerUserId, {
                body: incomingIntent === 'video' ? 'Video call' : 'Call',
                icon: logo
            });
            notification.onclick = () => {
                focusAppWindow();
                notification.close();
            };
        }
    }

    if (incomingIntent === 'video') {
        logger.info('Incoming Matrix video call', {source: 'call_video', event: 'incoming_received', callId, roomId});
    }
    dispatch(playRingtone());
    if (call.state === CallState.Ringing) sendRingingAcknowledgement(call, roomId);

    dispatch(setIncomingCall(true));

    console.log(`Matrix incoming call from ${callerName || callerUserId} in room ${roomId}`);
};

/**
 * Handle Matrix call state changes - equivalent to SIP session events
 */
const handleMatrixCallStateChange = (
    data: MatrixCallStateChangedData,
    dispatch: AppDispatch,
): void => {
    const {callId, roomId, newState, oldState} = data;
    const call = identityFromCurrentCall(callId, roomId);
    if (!call) return;

    if (newState === CallState.Ended) {
        handleMatrixCallEnded({
            callId,
            roomId,
            reason: data.hangupReason ?? data.newState,
            hangupParty: data.hangupParty,
            oldState,
        }, dispatch);
        return;
    }

    const coordinatorState = matrixCallCoordinator.getState();
    if (!isActiveCoordinatorCall(coordinatorState, call)) {
        logger.warn('Ignoring Matrix SDK state for a non-active call', {
            source: 'matrix_call',
            event: 'stale_engine_state_ignored',
            callId,
            roomId,
            sdkState: newState,
        });
        return;
    }

    switch (newState) {
        case CallState.WaitLocalMedia:
            matrixCallCoordinator.engineConnecting(call);
            dispatch(setCallState(CallStateEnum.CONNECTING));
            break;

        case CallState.InviteSent:
            matrixCallCoordinator.engineConnecting(call);
            awaitingRingingAckCallId = callId;
            if (bufferedRingingAckCallId === callId) {
                bufferedRingingAckCallId = null;
                preInviteCallId = null;
                onRemoteRingingAcknowledged(callId, dispatch);
            } else if (!store.getState().dialer.isConnectingSoundPlaying) {
                preInviteCallId = null;
                dispatch(playConnectingSound());
            }
            dispatch(setCallState(CallStateEnum.CONNECTING));
            break;

        case CallState.Ringing:
            dispatch(setCallState(CallStateEnum.CONNECTING));
            break;

        case CallState.Connecting:
            if (oldState === CallState.Connected) {
                matrixCallCoordinator.engineInterrupted(call);
            } else {
                matrixCallCoordinator.engineConnecting(call);
            }
            stopOutgoingCallSounds(dispatch);
            dispatch(setCallState(CallStateEnum.CONNECTING));
            break;

        case CallState.Connected:
            matrixCallCoordinator.engineConnected(call);
            if (matrixCallCoordinator.getState().kind !== 'connected') {
                return;
            }
            dispatch(stopRingtone());
            dispatch(setCallState(CallStateEnum.IN_CALL));
            stopOutgoingCallSounds(dispatch);
            dispatch(setIncomingCall(false));

            if (currentMatrixCall && matrixVoiceHandler) {
                console.log('Matrix call audio monitoring started');
            }
            break;

    }
};

const handleMatrixCallReplaced = (data: MatrixCallReplacedData): void => {
    const current = identityFromCurrentCall(data.oldCallId, data.roomId);
    if (!current || !isActiveCoordinatorCall(matrixCallCoordinator.getState(), current)) {
        logger.warn('Ignoring Matrix SDK replacement for a non-active call', {
            source: 'matrix_call',
            event: 'stale_engine_replacement_ignored',
            callId: data.oldCallId,
            replacementCallId: data.newCallId,
            roomId: data.roomId,
        });
        return;
    }

    const replacement = {
        ...current,
        matrixCallId: data.newCallId,
    };
    setCurrentMatrixCall(data.call);
    matrixCallCoordinator.engineReplaced(current, replacement);
    beginCallContext(data.newCallId, replacement.backendCallId);
    const callFrom: unknown = engineCallOf(store.getState().dialer, 'matrix')?.callFrom;
    if (typeof callFrom === 'object' && callFrom !== null) {
        tagEngine('matrix', store.dispatch)(setCallFrom({...callFrom, matrixCallId: data.newCallId}));
    }
    matrixVoiceHandler?.cleanupCall(data.oldCallId);
};

/**
 * Handle Matrix call feeds - equivalent to SIP track/stream events
 */
const handleMatrixCallFeeds = (data: MatrixCallFeedsChangedData): void => {
    const {callId, localFeeds, remoteFeeds} = data;

    const feedCall = currentMatrixCall;
    if (feedCall?.callId === callId) {
        const active = matrixCallCoordinator.getState();
        if (active.kind !== 'idle' && active.kind !== 'ended' && active.kind !== 'failed' && active.call.matrixCallId === callId) {
            const intent = matrixCallCoordinator.getMedia()?.intent ?? (feedCall.type === MatrixMediaCallType.Video ? 'video' : 'audio');
            matrixCallCoordinator.updateMedia(active.call, readMatrixCallMedia(feedCall, intent));
        }
        publishSession();
    }
    if (matrixVoiceHandler && currentMatrixCall?.callId === callId) {
        matrixVoiceHandler.handleCallFeedsChanged(callId, currentMatrixCall);

        console.log(`Matrix call ${callId} feeds updated:`, {
            local: localFeeds.length,
            remote: remoteFeeds.length
        });
    }
};

/**
 * Handle Matrix call errors - equivalent to SIP 'failed' event
 */
const handleMatrixCallError = (
    data: MatrixCallErrorData,
    dispatch: AppDispatch,
): void => {
    const {callId, error} = data;
    const call = identityFromCurrentCall(callId, data.roomId);

    if (!call || !isActiveCoordinatorCall(matrixCallCoordinator.getState(), call)) {
        logger.warn('Ignoring Matrix SDK error for a non-active call', {
            source: 'matrix_call',
            event: 'stale_engine_error_ignored',
            callId,
            roomId: data.roomId,
        });
        return;
    }

    const engineCall = currentMatrixCall?.callId === callId ? currentMatrixCall : null;
    if (data.action === undefined) {
        lastEngineError = data;
        logger.warn('Matrix call engine error', {
            source: 'matrix_call',
            event: 'engine_error',
            callId,
            roomId: call.roomId,
            sdkCode: data.code,
            sdkState: engineCall?.state,
            errorName: mediaErrorName(error),
            error: error instanceof Error ? error.message : String(error),
        });
        if (data.code === CallErrorCode.NoUserMedia && engineCall?.state === CallState.Connected) {
            dispatch(showToast(CAMERA_UNAVAILABLE_TOAST));
        }
        return;
    }

    if (engineCall && !engineCall.callHasEnded()) {
        logger.warn('Matrix call still live after a client failure; hanging it up', {
            source: 'matrix_call',
            event: 'engine_still_live',
            callId,
            roomId: call.roomId,
            sdkState: engineCall.state,
            action: data.action,
        });
        engineCall.hangup(CallErrorCode.UserHangup, false);
        return;
    }

    recordLocalSessionTermination(call, 'TECHNICAL_CLOSURE', data.action);
    matrixCallCoordinator.engineFailed(call, error);
    logger.error('Matrix call failed', {
        source: 'matrix_call',
        event: 'engine_failed',
        callId,
        roomId: call?.roomId,
        error: error instanceof Error ? error.message : String(error),
    });

    dispatch(setCallFrom(null));
    dispatch(setCallState(CallStateEnum.IDLE));
    stopOutgoingCallSounds(dispatch);
    dispatch(setIncomingCall(false));

    clearCallContext(call.matrixCallId);
    setCurrentMatrixCall(null);
    if (matrixVoiceHandler) {
        matrixVoiceHandler.cleanupCall(callId);
    }
};

const notifyPeerOfLocalFailure = (
    engineCall: MatrixCall | null,
    failure: {direction?: 'incoming' | 'outgoing'; reason: string; oldState?: CallState},
): void => {
    if (!engineCall || !isSilentLocalFailure(failure.reason)) return;
    if (failure.direction === 'outgoing' && (failure.oldState === undefined || STATES_BEFORE_INVITE.has(failure.oldState))) return;
    const unanswered = failure.direction === 'incoming' && failure.oldState !== undefined && STATES_BEFORE_ANSWER.has(failure.oldState);
    const notice = unanswered
        ? matrixService.sendCallReject(engineCall, failure.reason)
        : matrixService.sendCallHangup(engineCall, failure.reason);
    void notice.catch((error: unknown) => {
        logger.warn('Peer was not told about the local call failure', {
            source: 'matrix_call',
            event: 'failure_hangup_send_failed',
            callId: engineCall.callId,
            roomId: engineCall.roomId,
            error: error instanceof Error ? error.message : String(error),
        });
    });
};

/**
 * Handle Matrix call ended - equivalent to SIP 'ended' event
 */
const handleMatrixCallEnded = (
    data: MatrixCallEndedData,
    dispatch: AppDispatch,
): void => {
    const callId = data.callId ?? data.call_id ?? currentMatrixCall?.callId;
    const call = identityFromCurrentCall(callId, data.roomId);
    const coordinatorState = matrixCallCoordinator.getState();
    const isMatchingTerminalCall =
        coordinatorState.kind === 'ended' &&
        call !== null &&
        isSameMatrixCall(coordinatorState.call, call);

    if (!call || (!isActiveCoordinatorCall(coordinatorState, call) && !isMatchingTerminalCall)) {
        logger.warn('Ignoring Matrix SDK end event for a non-active call', {
            source: 'matrix_call',
            event: 'stale_engine_end_ignored',
            callId,
            roomId: data.roomId,
        });
        return;
    }

    if (lastFinalizedMatrixCallId === call.matrixCallId) return;
    lastFinalizedMatrixCallId = call.matrixCallId;

    const declinedByCallee =
        data.hangupParty === CallParty.Remote &&
        data.reason === 'user_hangup' &&
        coordinatorState.kind === 'connecting' &&
        coordinatorState.direction === 'outgoing';
    const engineError = lastEngineError?.callId === call.matrixCallId && lastEngineError.code === data.reason
        ? lastEngineError
        : undefined;
    const failureCause = data.hangupParty === CallParty.Local && engineError ? data.reason : undefined;
    if (!isMatchingTerminalCall) {
        if (failureCause) {
            matrixCallCoordinator.engineFailed(call, engineError?.error ?? failureCause);
        } else {
            matrixCallCoordinator.engineEnded(call, declinedByCallee ? MATRIX_CALL_DECLINED_REASON : data.reason ?? 'ended');
        }
    }
    logger.info('Matrix SDK call ended', {
        source: 'matrix_call',
        event: 'engine_ended',
        callId: call.matrixCallId,
        roomId: call.roomId,
        hangupParty: data.hangupParty,
        hangupReason: data.reason,
    });
    if (failureCause) {
        logger.error('Matrix call failed', {
            source: 'matrix_call',
            event: 'engine_failed',
            callId: call.matrixCallId,
            roomId: call.roomId,
            sdkCode: failureCause,
            sdkState: data.oldState,
            errorName: mediaErrorName(engineError?.error),
            error: engineError?.error instanceof Error ? engineError.error.message : String(engineError?.error),
        });
        const direction = 'direction' in coordinatorState ? coordinatorState.direction : undefined;
        notifyPeerOfLocalFailure(currentMatrixCall?.callId === call.matrixCallId ? currentMatrixCall : null, {
            direction, reason: failureCause, oldState: data.oldState,
        });
    }
    const terminalState = matrixCallCoordinator.getState();
    if (
        data.hangupParty !== CallParty.Remote &&
        !['answered_elsewhere', 'replaced'].includes(data.reason ?? '') &&
        (terminalState.kind !== 'ended' || terminalState.reason !== 'rejected')
    ) {
        recordBackendTermination(call, EventType.HANGUP, failureCause);
    }

    dispatch(stopRingtone());
    disconnectAudioRef.play();

    dispatch(setCallFrom(null));
    dispatch(setCallTo(null));
    dispatch(setCallState(CallStateEnum.IDLE));
    dispatch(setIncomingCall(false));
    stopOutgoingCallSounds(dispatch);

    const autoHideDialer = store.getState().dialer.autoHideDialer;
    if (autoHideDialer) {
        dispatch(setDialerHidden(true));
    }

    clearCallContext(call.matrixCallId);
    setCurrentMatrixCall(null);
    if (matrixVoiceHandler) {
        if (callId) {
            matrixVoiceHandler.cleanupCall(callId);
        }
    }
};

/**
 * Make outgoing Matrix call - equivalent to SIP call()
 */
/**
 * The Matrix account behind a PhoneHQ user. Display names are not unique (a
 * re-created account keeps the old name in the user directory), so a name
 * search can return the wrong person and the call then lands in a brand-new
 * DM with them. Prefer a Matrix ID the caller already knows, then the
 * backend's user-id mapping, and only fall back to the name search when both
 * are unavailable.
 */
export const resolvePeerMatrixId = async (
    user: Pick<User, 'userId' | 'firstName' | 'lastName'>,
    dispatch: AppDispatch,
    knownMatrixId?: string,
): Promise<string | null> => {
    if (knownMatrixId) return knownMatrixId;
    const userId = String(user.userId ?? '');
    if (userId) {
        try {
            const resolved = await dispatch(usersApi.endpoints.resolveUserIds.initiate({userIds: [userId]})).unwrap();
            const matrixId = resolved[userId];
            if (matrixId && /^@[^:]+:.+$/.test(matrixId)) return matrixId;
        } catch (error) {
            logger.warn('Could not map the user to a Matrix ID; falling back to the name search', {
                source: 'matrix_call',
                event: 'peer_matrix_id_lookup_failed',
                error: error instanceof Error ? error.message : String(error),
            });
        }
    }
    return matrixService.searchMatrixIdByName(user.firstName ?? '', user.lastName ?? '');
};

export const makeMatrixCall = async (
    user: User,
    currentUserId: string,
    tenant: string,
    dispatch: AppDispatch,
    conversationId?: string | undefined,
    options: {video?: boolean; peerMatrixId?: string} = {},
) => {
    if (outgoingSetupInFlight || getActiveMatrixCallIdentity()) throw new Error('A Matrix call is already active');
    if (store.getState().dialer.callState === CallStateEnum.GROUP_CALL) throw new Error('A group call is active');
    dispatch = tagEngine('matrix', dispatch);
    const requestedVideo = options.video === true;
    outgoingSetupInFlight = true;
    try {
        const permissions = await readCallPermissions(requestedVideo);
        logger.info('Matrix call media permissions', {
            source: 'matrix_call', event: 'media_permissions', direction: 'outgoing', ...permissions,
        });
        if (permissions.microphone === 'denied') {
            dispatch(showToast(MICROPHONE_BLOCKED_TOAST));
            throw new Error('Microphone access is blocked');
        }
        const video = requestedVideo && permissions.camera !== 'denied';
        if (requestedVideo && !video) {
            dispatch(showToast(CAMERA_UNAVAILABLE_TOAST));
            logger.info('Outgoing Matrix video call downgraded to audio', {
                source: 'call_video', event: 'video_downgraded', reason: 'camera_blocked',
            });
        }
        dispatch(setCallState(CallStateEnum.CONNECTING));
        dispatch(setCallFrom({...user}));
        dispatch(setDialerHidden(false));
        let roomId = conversationId;
        if (!roomId) {
            const canResolvePeer = Boolean(options.peerMatrixId || user.userId || (user.firstName && user.lastName));
            if (!canResolvePeer || !tenant) {
                throw new Error('Cannot make call: Missing roomId or user information');
            }

            const matrixId = await resolvePeerMatrixId(user, dispatch, options.peerMatrixId);
            if (!matrixId) {
                throw new Error(`Could not find Matrix ID for user ${user.firstName} ${user.lastName}`);
            }

            // Reuse the existing direct room. Without this every call from the
            // dialer created another one: the caller and callee accumulated a
            // room per call, none of them carrying the conversation, and the
            // fresh room has no member profiles yet — which is why the callee
            // saw an internal id instead of a name on the incoming call.
            roomId = matrixService.findDirectMessageRoom([matrixId]);

            if (!roomId) {
                roomId = await matrixService.createRoom({
                    invite: [matrixId],
                    isDirectMessage: true
                });
            }
        }
        const ownerId = getAuthenticatedUserId() ?? Number(currentUserId);
        if (!Number.isFinite(ownerId) || ownerId <= 0) {
            throw new Error('Cannot make call: current user ID is not available');
        }

        const resultAction = await dispatch(fetchCallMetadata({
            roomId,
            type: CallType.INTERNAL
        })).unwrap();

        await dispatch(setCurrentCallData({
            callId: resultAction.callId,
            caller: ownerId,
            callees: [user.userId],
            roomId,
            startedAt: Date.now(),
            endedAt: null,
            status: 'ONGOING'
        }));
        const matrixClient = matrixService.getClient();
        if (!matrixClient) throw new Error('Matrix client is not available');
        lastFinalizedMatrixCallId = null;
        if (video) {
            logger.info('Outgoing Matrix video call', {source: 'call_video', event: 'outgoing_started', roomId});
        }
        const callIdentity = await matrixCallCoordinator.startVoiceCall({
            roomId,
            backendCallId: resultAction.callId,
            video,
            beforeSignalling: async identity => {
                const context = captureAccountingContext(identity, resultAction);
                await sendMatrixEvent({type: EventType.INVITE, ...context, occurredAt: Date.now(),
                    caller: ownerId, callees: [user.userId], ...(video ? {media: 'video' as const} : {})});
                await matrixClient.sendEvent(identity.roomId, MATRIX_CALL_METADATA_EVENT_TYPE,
                    {...resultAction, matrixCallId: identity.matrixCallId, clientSessionId: context.clientSessionId,
                        ...(video ? {media: 'video' as const} : {})});
            },
        });
        lastFinalizedMatrixCallId = null;
            const call = currentMatrixCall;
        if (!call) {
            throw new Error('Matrix call engine did not expose the active call');
        }
        dispatch(setCallFrom({...user, roomId, matrixCallId: callIdentity.matrixCallId}));
        dispatch(setCallState(CallStateEnum.CONNECTING));
        if (call.state === CallState.Connected) {
            matrixCallCoordinator.engineConnected(callIdentity);
            dispatch(setCallState(CallStateEnum.IN_CALL));
            stopOutgoingCallSounds(dispatch);
        }


        return call;

    } catch (error) {
        console.error('Failed to make Matrix call:', error);
        const failed = matrixCallCoordinator.getState();
        if (failed.kind === 'failed' && failed.call) {
            recordBackendTermination(failed.call, EventType.HANGUP);
            void matrixService.hangupCall(failed.call.matrixCallId).catch(() => {
                logger.warn('Cancelled call signaling remains unavailable', {source: 'matrix_call', event: 'setup_cancel_signaling_failed'});
            });
            clearCallContext(failed.call.matrixCallId);
            setCurrentMatrixCall(null);
        }
        stopOutgoingCallSounds(dispatch);
        dispatch(setCallState(CallStateEnum.IDLE));
        throw error;
    } finally {
        outgoingSetupInFlight = false;
    }
};

/**
 * Answer Matrix call - equivalent to SIP answerCall()
 */
export const answerMatrixCall = async (
    callId: string,
    dispatch: AppDispatch,
    options: {video?: boolean} = {},
) => {
    dispatch = tagEngine('matrix', dispatch);
    try {
        dispatch(stopRingtone());

        const activeState = matrixCallCoordinator.getState();
        if (
            activeState.kind === 'idle' ||
            activeState.kind === 'ended' ||
            activeState.kind === 'failed' ||
            activeState.call.matrixCallId !== callId
        ) {
            throw new Error(`Incoming Matrix call ${callId} is not active`);
        }
        const requestedVideo = options.video ?? matrixCallCoordinator.getMedia()?.intent === 'video';
        const permissions = await readCallPermissions(requestedVideo);
        logger.info('Matrix call media permissions', {
            source: 'matrix_call', event: 'media_permissions', callId, direction: 'incoming', ...permissions,
        });
        const answerWithVideo = requestedVideo && permissions.camera !== 'denied';
        if (requestedVideo && !answerWithVideo) {
            dispatch(showToast(CAMERA_UNAVAILABLE_TOAST));
        }
        await matrixCallCoordinator.accept({video: answerWithVideo});
        const accepted = matrixCallCoordinator.getState();
        if ((accepted.kind !== 'connecting' && accepted.kind !== 'connected') || accepted.call.matrixCallId !== callId) {
            const engineError = lastEngineError?.callId === callId ? lastEngineError : undefined;
            logger.warn('Matrix call answer failed', {
                source: 'matrix_call',
                event: 'answer_failed',
                callId,
                state: accepted.kind,
                requestedVideo: answerWithVideo,
                sdkCode: engineError?.code,
                errorName: mediaErrorName(engineError?.error),
                ...permissions,
            });
            return;
        }
        if (answerWithVideo) {
            logger.info('Matrix video call answered', {source: 'call_video', event: 'answered', callId});
        }

        dispatch(setCallState(CallStateEnum.CONNECTING));
        dispatch(setIncomingCall(false));

        const roomId = currentMatrixCall?.roomId;
        const metadataContent = roomId
            ? readIncomingCallMetadata(roomId, callId)
            : null;
        if (metadataContent) {
            dispatch(setCallMetadata(metadataContent));
        }

        const context = accountingContexts.get(callId) ?? (metadataContent ? captureAccountingContext(activeState.call, metadataContent) : undefined);
        if (context) void sendMatrixEvent({type: EventType.ANSWER, ...context, occurredAt: Date.now()})
            .catch(() => logger.warn('Matrix answer accounting failed', {source: 'matrix_call', event: 'answer_accounting_failed', callId}));

    } catch (error) {
        console.error('Failed to answer Matrix call:', error);
        dispatch(setCallState(CallStateEnum.IDLE));
        throw error;
    }
};

/**
 * Reject Matrix call - equivalent to SIP rejectCall()
 */
export const rejectMatrixCall = async (
    callId: string,
    dispatch: AppDispatch
) => {
    dispatch = tagEngine('matrix', dispatch);
    try {
        dispatch(stopRingtone());

        const roomId = currentMatrixCall?.roomId;
        const metadataContent = roomId
            ? readIncomingCallMetadata(roomId, callId)
            : null;
        if (metadataContent) {
            dispatch(setCallMetadata(metadataContent));
        }

        const activeState = matrixCallCoordinator.getState();
        if (
            activeState.kind === 'idle' ||
            activeState.kind === 'ended' ||
            activeState.kind === 'failed' ||
            activeState.call.matrixCallId !== callId
        ) {
            throw new Error(`Incoming Matrix call ${callId} is not active`);
        }
        recordBackendTermination(activeState.call, EventType.REJECT);
        await matrixCallCoordinator.reject();

        dispatch(setIncomingCall(false));
        dispatch(setCallFrom(null));
        dispatch(setCallState(CallStateEnum.IDLE));

        setCurrentMatrixCall(null);



    } catch (error) {
        console.error('Failed to reject Matrix call:', error);
        throw error;
    }
};

/**
 * End Matrix call - equivalent to SIP endCall()
 */
export const endMatrixCall = async (
    callId: string,
    dispatch: AppDispatch
) => {
    dispatch = tagEngine('matrix', dispatch);
    try {
        dispatch(setIncomingCall(false));
        dispatch(setCallFrom(null));
        dispatch(setCallState(CallStateEnum.IDLE));
        const activeState = matrixCallCoordinator.getState();
        if (
            activeState.kind !== 'idle' &&
            activeState.kind !== 'ended' &&
            activeState.kind !== 'failed' &&
            activeState.call.matrixCallId === callId
        ) {
            recordBackendTermination(activeState.call, EventType.HANGUP);
            await matrixCallCoordinator.hangUp();
            handleMatrixCallEnded(
                {
                    callId: activeState.call.matrixCallId,
                    roomId: activeState.call.roomId,
                    reason: 'local_hangup',
                },
                dispatch,
            );
        }


    } catch (error) {
        console.error('Failed to end Matrix call:', error);
        throw error;
    }
};

/**
 * Set Matrix call volume - equivalent to SIP setVolume()
 */
export const setMatrixCallVolume = (volume: number) => {
    if (volume < 0 || volume > 1) {
        console.warn('Volume should be between 0.0 and 1.0');
        return;
    }

    if (matrixVoiceHandler && currentMatrixCall) {
        matrixCallCoordinator.setOutputVolume(volume);
        console.log(`Matrix call volume set to: ${volume}`);
    }
};

/**
 * Toggle Matrix call mute - equivalent to SIP toggleMuted()
 */
export const toggleMatrixCallMute = async () => {
    if (!currentMatrixCall) return;

    try {
        const isMuted = currentMatrixCall.isMicrophoneMuted();
        await matrixCallCoordinator.setMuted(!isMuted);
        console.log(`Matrix call ${isMuted ? 'unmuted' : 'muted'}`);
    } catch (error) {
        console.error('Failed to toggle Matrix call mute:', error);
    }
};

export const setMatrixCallHold = (held: boolean): void => {
    if (!currentMatrixCall) return;
    try {
        currentMatrixCall.setRemoteOnHold(held);
        logger.info('Matrix call hold changed', {source: 'call_video', event: 'hold_changed', held});
    } catch (error) {
        logger.warn('Matrix call hold failed', {
            source: 'call_video',
            event: 'hold_failed',
            error: error instanceof Error ? error.message : String(error),
        });
    }
};

export const toggleMatrixCallHold = (): void => {
    if (!currentMatrixCall) return;
    setMatrixCallHold(!currentMatrixCall.isRemoteOnHold());
};

export const sendMatrixCallDtmf = (digit: string): boolean => {
    if (!currentMatrixCall?.opponentSupportsDTMF()) return false;
    try {
        currentMatrixCall.sendDtmfDigit(digit);
        return true;
    } catch (error) {
        logger.warn('Matrix call DTMF failed', {
            source: 'call_video',
            event: 'dtmf_failed',
            error: error instanceof Error ? error.message : String(error),
        });
        return false;
    }
};

export const setMatrixCallOutputDevice = async (deviceId: string): Promise<void> => {
    const element = currentMatrixCall ? matrixVoiceHandler?.getAudioElement(currentMatrixCall.callId, 'remote') : null;
    if (!element || !('setSinkId' in element)) return;
    try {
        await (element as HTMLAudioElement & {setSinkId(id: string): Promise<void>}).setSinkId(deviceId);
    } catch (error) {
        logger.warn('Matrix call output device failed', {
            source: 'call_video',
            event: 'output_device_failed',
            error: error instanceof Error ? error.message : String(error),
        });
    }
};

export const setMatrixCallCamera = async (enabled: boolean): Promise<boolean> => {
    const callId = currentMatrixCall?.callId;
    const cameraPermission = enabled ? await readMediaPermission('camera') : undefined;
    logger.info('Matrix call camera toggle requested', {
        source: 'call_video', event: 'camera_toggle_requested', callId, enabled, cameraPermission,
    });
    if (cameraPermission === 'denied') {
        store.dispatch(showToast(CAMERA_UNAVAILABLE_TOAST));
        logger.warn('Matrix call camera is blocked', {
            source: 'call_video', event: 'camera_toggle_failed', callId, enabled, cameraPermission, reason: 'permission_denied',
        });
        return false;
    }
    const errorBefore = lastEngineError;
    try {
        const active = await matrixCallCoordinator.setLocalVideo(enabled);
        logger.info('Matrix call camera toggled', {source: 'call_video', event: 'camera_toggled', callId, enabled: active});
        if (enabled && !active) {
            const engineError = lastEngineError !== errorBefore ? lastEngineError : null;
            logger.warn('Matrix call camera did not start', {
                source: 'call_video',
                event: 'camera_toggle_failed',
                callId,
                enabled,
                cameraPermission,
                sdkCode: engineError?.code,
                errorName: mediaErrorName(engineError?.error),
            });
        }
        return active;
    } catch (error) {
        logger.warn('Matrix call camera toggle failed', {
            source: 'call_video',
            event: 'camera_toggle_failed',
            callId,
            error: error instanceof Error ? error.message : String(error),
        });
        return false;
    }
};

export const setMatrixCallScreenShare = async (enabled: boolean): Promise<boolean> => {
    try {
        const active = await matrixCallCoordinator.setScreenSharing(enabled);
        logger.info('Matrix call screen share toggled', {source: 'call_video', event: 'screen_share_toggled', enabled: active});
        return active;
    } catch (error) {
        logger.warn('Matrix call screen share failed', {
            source: 'call_video',
            event: 'screen_share_failed',
            error: error instanceof Error ? error.message : String(error),
        });
        return false;
    }
};

export const switchMatrixCallDevice = async (kind: MediaDeviceKind, deviceId: string): Promise<void> => {
    const handler = matrixService.getClient()?.getMediaHandler();
    if (!handler) return;
    try {
        if (kind === 'audioinput') {
            await handler.setAudioInput(deviceId);
        } else if (kind === 'videoinput') {
            await handler.setVideoInput(deviceId);
        }
        logger.info('Matrix call device switched', {source: 'call_video', event: 'device_switched', kind});
    } catch (error) {
        logger.warn('Matrix call device switch failed', {
            source: 'call_video',
            event: 'device_switch_failed',
            kind,
            error: error instanceof Error ? error.message : String(error),
        });
    }
};
